const PICK5_RULES_VERSION = "florida-pick5-v2026-07";

const PICK5_RULES = {
  straightPrize: 50000,
  boxPrizes: { 5: 10000, 10: 5000, 20: 2500, 30: 1660, 60: 830, 120: 416 },
  straightBoxStraightPrizes: { 5: 30000, 10: 27500, 20: 26250, 30: 25830, 60: 25415, 120: 25208 },
  fireballStraightPrize: 12000,
  fireballBoxPrizes: { 5: 2400, 10: 1200, 20: 600, 30: 400, 60: 200, 120: 100 },
  fireballStraightBoxStraightPrizes: { 5: 7200, 10: 6600, 20: 6300, 30: 6200, 60: 6100, 120: 6050 },
};

function toNum(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function roundMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function factorial(n) {
  let out = 1;
  for (let i = 2; i <= n; i++) out *= i;
  return out;
}

function permutationCount(nums) {
  const values = Array.isArray(nums) ? nums : [];
  const counts = {};
  values.forEach(n => { counts[n] = (counts[n] || 0) + 1; });
  return Object.values(counts).reduce((ways, count) => ways / factorial(count), factorial(values.length));
}

function sameMultiset(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return [...a].sort((x, y) => x - y).join("-") === [...b].sort((x, y) => x - y).join("-");
}

function uniqueFireballVariants(drawNumbers, fireballDigit) {
  const fireball = Number(fireballDigit);
  if (!Number.isInteger(fireball) || fireball < 0 || fireball > 9) return [];
  const seen = new Set();
  const variants = [];
  for (let i = 0; i < drawNumbers.length; i++) {
    const next = [...drawNumbers];
    next[i] = fireball;
    const key = next.join("-");
    if (seen.has(key)) continue;
    seen.add(key);
    variants.push(next);
  }
  return variants;
}

function pickRuleValue(...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== "") return value;
  }
  return undefined;
}

function toBool(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const text = value.trim().toLowerCase();
    if (["false", "0", "no", "off"].includes(text)) return false;
    if (["true", "1", "yes", "on"].includes(text)) return true;
  }
  return !!value;
}

function normalizePick5PlayType(value) {
  const text = String(value || "").trim().toLowerCase().replace(/[_-]+/g, " ");
  if (text === "box") return "Box";
  if (text === "straight/box" || text === "straight box") return "Straight/Box";
  return "Straight";
}

function normalizePick5Rules(entry, fallbackRules = {}) {
  const nested = entry?.pickRules || entry?.rules || entry?.playRules || {};
  const source = nested && typeof nested === "object" ? nested : {};
  const fallback = fallbackRules && typeof fallbackRules === "object" ? fallbackRules : {};
  const playType = normalizePick5PlayType(pickRuleValue(
    entry?.playType,
    entry?.play_type,
    source.playType,
    source.play_type,
    fallback.playType,
    fallback.play_type,
    "Straight"
  ));
  const rawWager = Number(pickRuleValue(entry?.wager, entry?.amount, source.wager, source.amount, fallback.wager, fallback.amount, 1));
  const rawMultiple = Number(pickRuleValue(entry?.multiple, entry?.multiplier, source.multiple, source.multiplier, fallback.multiple, fallback.multiplier, 1));
  const rawCost = pickRuleValue(entry?.cost, entry?.representedCost, source.cost, source.representedCost);
  const rawWays = Number(pickRuleValue(entry?.permutationCount, entry?.ways, source.permutationCount, source.ways));
  const rawTickets = Number(pickRuleValue(entry?.representedTickets, entry?.represented_plays, source.representedTickets, source.represented_plays));
  const wager = Number.isFinite(rawWager) && rawWager > 0 ? rawWager : 1;
  return {
    playType,
    wager: wager === 0.5 ? 0.5 : 1,
    multiple: Number.isFinite(rawMultiple) && rawMultiple > 0 ? rawMultiple : 1,
    fireball: toBool(pickRuleValue(entry?.fireball, entry?.fireballEnabled, source.fireball, source.fireballEnabled, fallback.fireball, fallback.fireballEnabled, false)),
    cost: rawCost === undefined ? null : toNum(rawCost, null),
    permutationCount: Number.isFinite(rawWays) && rawWays > 0 ? rawWays : null,
    representedTickets: Number.isFinite(rawTickets) && rawTickets >= 0 ? rawTickets : null,
  };
}

function pick5LineCost(numbers, rules) {
  if (rules.cost !== null && rules.cost >= 0) return rules.cost;
  const ticketWager = rules.playType === "Straight/Box" ? 1 : rules.wager;
  return ticketWager * rules.multiple * (rules.fireball ? 2 : 1);
}

function pick5RepresentedTickets(rules) {
  if (rules.representedTickets !== null && rules.representedTickets >= 0) return rules.representedTickets;
  return rules.multiple;
}

function makePath(common, winPath, payoutBeforeMultiplier, category) {
  const before = roundMoney(payoutBeforeMultiplier);
  return {
    ...common,
    win_path: winPath,
    category,
    payout_before_multiplier: before,
    multiplier: common.multiple,
    final_payout: roundMoney(before * common.multiple),
  };
}

function scorePick5Line(entry, draw, fallbackRules = {}, index = 0) {
  const numbers = Array.isArray(entry?.numbers) ? entry.numbers.map(Number).filter(Number.isFinite) : [];
  const drawNumbers = Array.isArray(draw?.numbers) ? draw.numbers.map(Number).filter(Number.isFinite) : [];
  if (numbers.length !== 5 || drawNumbers.length !== 5) return null;
  const rawFireball = draw?.fireball_digit;
  const fireball = rawFireball === null || rawFireball === undefined || rawFireball === "" ? NaN : Number(rawFireball);
  const rules = normalizePick5Rules(entry, fallbackRules);
  const ways = rules.permutationCount || permutationCount(numbers);
  const validBox = ways > 1;
  const type = rules.playType;
  const ticketWager = type === "Straight/Box" ? 1 : rules.wager;
  const exact = numbers.every((n, i) => Number(n) === drawNumbers[i]);
  const boxed = sameMultiset(numbers, drawNumbers);
  const variants = rules.fireball ? uniqueFireballVariants(drawNumbers, fireball) : [];
  const playKey = numbers.join("-");
  const playBoxKey = [...numbers].sort((a, b) => a - b).join("-");
  const fireballExactCount = variants.filter(variant => variant.join("-") === playKey).length;
  const fireballBoxCount = validBox
    ? variants.filter(variant => variant.join("-") !== playKey && [...variant].sort((a, b) => a - b).join("-") === playBoxKey).length
    : 0;
  const positionMatch = numbers.reduce((sum, n, i) => sum + (Number(n) === drawNumbers[i] ? 1 : 0), 0);
  const common = {
    saved_digits: numbers,
    saved_line_index: index + 1,
    play_type: type,
    wager: rules.wager,
    multiple: rules.multiple,
    fireball_enabled: !!rules.fireball,
    official_base_digits: drawNumbers,
    official_fireball_digit: Number.isInteger(fireball) ? fireball : null,
    permutation_count: ways,
  };
  const paths = [];

  if (type === "Straight") {
    if (exact) paths.push(makePath(common, "Base Straight", PICK5_RULES.straightPrize * ticketWager, "straight"));
    for (let i = 0; i < fireballExactCount; i++) paths.push(makePath(common, "Fireball Straight", PICK5_RULES.fireballStraightPrize * ticketWager, "fireball"));
  } else if (type === "Box") {
    if (boxed && validBox) paths.push(makePath(common, "Base Box", (PICK5_RULES.boxPrizes[ways] || 0) * ticketWager, "box"));
    for (let i = 0; i < fireballBoxCount; i++) paths.push(makePath(common, "Fireball Box", (PICK5_RULES.fireballBoxPrizes[ways] || 0) * ticketWager, "fireball"));
  } else if (type === "Straight/Box") {
    if (exact) paths.push(makePath(common, "Straight/Box Straight portion", PICK5_RULES.straightBoxStraightPrizes[ways] || 0, "straight_box"));
    else if (boxed && validBox) paths.push(makePath(common, "Straight/Box Box portion", (PICK5_RULES.boxPrizes[ways] || 0) * 0.5, "straight_box"));
    for (let i = 0; i < fireballExactCount; i++) paths.push(makePath(common, "Fireball Straight/Box Straight portion", PICK5_RULES.fireballStraightBoxStraightPrizes[ways] || 0, "fireball"));
    for (let i = 0; i < fireballBoxCount; i++) paths.push(makePath(common, "Fireball Straight/Box Box portion", (PICK5_RULES.fireballBoxPrizes[ways] || 0) * 0.5, "fireball"));
  }

  const base = paths.filter(path => path.category !== "fireball").reduce((sum, path) => sum + path.final_payout, 0);
  const fireballAmount = paths.filter(path => path.category === "fireball").reduce((sum, path) => sum + path.final_payout, 0);
  const cash = roundMoney(base + fireballAmount);
  return {
    numbers,
    rules,
    paths: paths.filter(path => path.final_payout > 0),
    base: roundMoney(base),
    fireball: roundMoney(fireballAmount),
    cash,
    bestMatch: cash > 0 ? 5 : positionMatch,
    lineCost: roundMoney(pick5LineCost(numbers, rules)),
    representedTickets: pick5RepresentedTickets(rules),
    permutationCount: ways,
  };
}

function emptyPick5MatchCounts() {
  return { "0": 0, "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 };
}

function addBucket(buckets, key, details, cost, represented) {
  if (!buckets[key]) {
    buckets[key] = { label: key, saved_lines: 0, represented_tickets: 0, cost: 0, cash: 0, base_cash: 0, fireball_cash: 0, base_winning_lines: 0, fireball_winning_lines: 0 };
  }
  const bucket = buckets[key];
  bucket.saved_lines += 1;
  bucket.represented_tickets += represented;
  bucket.cost += cost;
  bucket.cash += details.cash;
  bucket.base_cash += details.base;
  bucket.fireball_cash += details.fireball;
  if (details.base > 0) bucket.base_winning_lines += 1;
  if (details.fireball > 0) bucket.fireball_winning_lines += 1;
}

function entriesRequireFireball(entries = [], fallbackRules = {}) {
  const list = Array.isArray(entries) ? entries : [];
  if (list.length) return list.some(entry => normalizePick5Rules(entry, fallbackRules).fireball);
  return normalizePick5Rules({}, fallbackRules).fireball;
}

function calculatePick5EntriesDraw(entries, draw, fallbackRules = {}) {
  const drawNumbers = Array.isArray(draw?.numbers) ? draw.numbers.map(Number).filter(Number.isFinite) : [];
  const rawFireball = draw?.fireball_digit;
  const fireball = rawFireball === null || rawFireball === undefined || rawFireball === "" ? NaN : Number(rawFireball);
  const requiresFireball = entriesRequireFireball(entries, fallbackRules);
  const hasValidFireball = Number.isInteger(fireball) && fireball >= 0 && fireball <= 9;
  if (drawNumbers.length !== 5) return { error: "Official Pick 5 draw does not have 5 winning digits" };

  const matchCounts = emptyPick5MatchCounts();
  const prizeBreakdown = {
    saved_lines_checked: 0,
    represented_tickets: 0,
    straight_cash: 0,
    box_cash: 0,
    straight_box_cash: 0,
    fireball_cash: 0,
    fireball_status: requiresFireball ? (hasValidFireball ? "scored" : "unavailable") : "not_selected",
    buckets: {},
    rule_version: PICK5_RULES_VERSION,
  };
  let savedLines = 0;
  let representedTickets = 0;
  let totalCost = 0;
  let cashWon = 0;
  let winningPlaysCount = 0;
  let bestMatch = 0;
  let largestPrize = 0;
  const winningPlayDetails = [];

  (entries || []).forEach((entry, index) => {
    const details = scorePick5Line(entry, draw, fallbackRules, index);
    if (!details) return;
    savedLines += 1;
    representedTickets += details.representedTickets;
    totalCost += details.lineCost;
    cashWon += details.cash;
    bestMatch = Math.max(bestMatch, details.bestMatch);
    largestPrize = Math.max(largestPrize, details.cash);
    matchCounts[String(Math.max(0, Math.min(5, details.bestMatch)))] += 1;
    if (details.cash > 0) {
      winningPlaysCount += 1;
      winningPlayDetails.push(...details.paths);
    }
    addBucket(prizeBreakdown.buckets, details.rules.playType, details, details.lineCost, details.representedTickets);
    if (details.rules.playType === "Straight") prizeBreakdown.straight_cash += details.base;
    else if (details.rules.playType === "Box") prizeBreakdown.box_cash += details.base;
    else if (details.rules.playType === "Straight/Box") prizeBreakdown.straight_box_cash += details.base;
    prizeBreakdown.fireball_cash += details.fireball;
  });

  prizeBreakdown.saved_lines_checked = savedLines;
  prizeBreakdown.represented_tickets = representedTickets;
  for (const bucket of Object.values(prizeBreakdown.buckets)) {
    bucket.cost = roundMoney(bucket.cost);
    bucket.cash = roundMoney(bucket.cash);
    bucket.base_cash = roundMoney(bucket.base_cash);
    bucket.fireball_cash = roundMoney(bucket.fireball_cash);
  }
  ["straight_cash", "box_cash", "straight_box_cash", "fireball_cash"].forEach(key => {
    prizeBreakdown[key] = roundMoney(prizeBreakdown[key]);
  });

  return {
    play_count: savedLines,
    represented_plays: representedTickets,
    total_cost: roundMoney(totalCost),
    cash_won: roundMoney(cashWon),
    net: roundMoney(cashWon - totalCost),
    roi: totalCost > 0 ? ((cashWon - totalCost) / totalCost) * 100 : null,
    best_match: bestMatch,
    free_tickets: 0,
    largest_prize: roundMoney(largestPrize),
    winning_plays_count: winningPlaysCount,
    match_counts: matchCounts,
    prize_breakdown: prizeBreakdown,
    winning_play_details: winningPlayDetails,
    fireball_status: prizeBreakdown.fireball_status,
  };
}

module.exports = {
  PICK5_RULES,
  PICK5_RULES_VERSION,
  calculatePick5EntriesDraw,
  normalizePick5Rules,
  pick5LineCost,
  pick5RepresentedTickets,
  scorePick5Line,
  uniqueFireballVariants,
};
